import ObjectTypeModel from 'cio/models/object-type';
import { sortByCaseInsensitive } from './sort';

export const ActivityLogWildcardString = '$1:*:';
export const ActivityLogWildcardRegex = /^(_(o|r)):([^:]*):/;

export enum ActivityLogType {
  AttemptedAction = 'attempted_action',
  AttributeChange = 'attribute_change',
  FailedAttributeChange = 'failed_attribute_change',
  FailedBatchUpdate = 'failed_batch_update',
  SkippedUpdate = 'skipped_update',
  FailedQueryCollection = 'failed_query_collection',
  Event = 'event',
  FailedEvent = 'failed_event',
  Page = 'page',
  DraftedEmail = 'drafted_email',
  AttemptedEmail = 'attempted_email',
  SentEmail = 'sent_email',
  DeliveredEmail = 'delivered_email',
  OpenedEmail = 'opened_email',
  ClickedEmail = 'clicked_email',
  ConvertedEmail = 'converted_email',
  BouncedEmail = 'bounced_email',
  FailedEmail = 'failed_email',
  UnsubscribedEmail = 'unsubscribed_email',
  TopicUnsubscribedEmail = 'topic_unsubscribed_email',
  SpammedEmail = 'spammed_email',
  DroppedEmail = 'dropped_email',
  UndeliverableEmail = 'undeliverable_email',
  DraftedAction = 'drafted_action',
  DeliveredAction = 'delivered_action',
  BouncedAction = 'bounced_action',
  SentAction = 'sent_action',
  OpenedAction = 'opened_action',
  ClickedAction = 'clicked_action',
  FailedAction = 'failed_action',
  ConvertedAction = 'converted_action',
  DeviceChange = 'device_change',
  DeferredEmail = 'deferred_email',
  UnsubscribedAction = 'unsubscribed_action',
  DroppedAction = 'dropped_action',
  UndeliverableAction = 'undeliverable_action',
  ProfileCreate = 'profile_create',
  ProfileDelete = 'profile_delete',
  ProfileMerge = 'profile_merge',
  AnonMerge = 'anon_merge',
  Screen = 'screen',
  WebhookEvent = 'webhook_event',
  PersonAddRelationship = 'add_relationship',
  PersonDeleteRelationship = 'delete_relationship',
  ObjectCreate = '_o:*:create',
  ObjectAttributeChange = '_o:*:attribute_change',
  ObjectFailedAttributeChange = '_o:*:failed_attribute_change',
  ObjectDelete = '_o:*:delete',
  ObjectsAddRelationship = '_o:*:add_relationship',
  ObjectsDeleteRelationship = '_o:*:delete_relationship',
  RelationshipAttributeChange = '_r:*:attribute_change',
  RelationshipFailedAttributeChange = '_r:*:failed_attribute_change',
  DeferredAction = 'deferred_action',
  FailedObjectJourneys = 'failed_object_journeys',
}

export const LogTypeNames = {
  [ActivityLogType.AttemptedAction]: 'Attempted to Send Action',
  [ActivityLogType.AttributeChange]: 'Attribute Change',
  [ActivityLogType.FailedAttributeChange]: 'Failed Attribute Change',
  [ActivityLogType.FailedBatchUpdate]: 'Failed Batch Update',
  [ActivityLogType.SkippedUpdate]: 'Skipped Update',
  [ActivityLogType.FailedQueryCollection]: 'Failed Collection Query',
  [ActivityLogType.Event]: 'Event',
  [ActivityLogType.FailedEvent]: 'Failed Event',
  [ActivityLogType.Page]: 'Page View',
  [ActivityLogType.DraftedEmail]: 'Drafted Email',
  [ActivityLogType.AttemptedEmail]: 'Attempted To Send Email',
  [ActivityLogType.SentEmail]: 'Sent Email',
  [ActivityLogType.DeliveredEmail]: 'Delivered Email',
  [ActivityLogType.OpenedEmail]: 'Opened Email',
  [ActivityLogType.ClickedEmail]: 'Clicked Email',
  [ActivityLogType.ConvertedEmail]: 'Converted Email',
  [ActivityLogType.BouncedEmail]: 'Bounced Email',
  [ActivityLogType.FailedEmail]: 'Failed to Send Email',
  [ActivityLogType.UnsubscribedEmail]: 'Unsubscribed Email',
  [ActivityLogType.TopicUnsubscribedEmail]: 'Topics Unsubscribed Email',
  [ActivityLogType.SpammedEmail]: 'Spammed Email',
  [ActivityLogType.DroppedEmail]: 'Suppressed Email',
  [ActivityLogType.UndeliverableEmail]: 'Undeliverable Email',
  [ActivityLogType.DraftedAction]: 'Drafted Action',
  [ActivityLogType.DeliveredAction]: 'Delivered Action',
  [ActivityLogType.BouncedAction]: 'Bounced Action',
  [ActivityLogType.SentAction]: 'Sent Action',
  [ActivityLogType.OpenedAction]: 'Opened Action',
  [ActivityLogType.ClickedAction]: 'Clicked Action',
  [ActivityLogType.FailedAction]: 'Failed Action',
  [ActivityLogType.ConvertedAction]: 'Converted Action',
  [ActivityLogType.DeviceChange]: 'Device Change',
  [ActivityLogType.DeferredEmail]: 'Deferred Email',
  [ActivityLogType.UnsubscribedAction]: 'Unsubscribed Action',
  [ActivityLogType.DroppedAction]: 'Suppressed Action', // there is a mismatch between UI state name and BE: https://github.com/customerio/issues/issues/2124
  [ActivityLogType.UndeliverableAction]: 'Undeliverable Action',
  [ActivityLogType.ProfileCreate]: 'Person Created',
  [ActivityLogType.ProfileDelete]: 'Person Deleted',
  [ActivityLogType.ProfileMerge]: 'People Merged',
  [ActivityLogType.AnonMerge]: 'Anonymous Data Merged',
  [ActivityLogType.Screen]: 'Screen View',
  [ActivityLogType.WebhookEvent]: 'Webhook Arrived',
  [ActivityLogType.PersonAddRelationship]: 'Relationship Created (to Object)',
  [ActivityLogType.PersonDeleteRelationship]: 'Relationship Deleted (to Object)',
  [ActivityLogType.ObjectCreate]: 'Object Created',
  [ActivityLogType.ObjectAttributeChange]: 'Object Attribute Change',
  [ActivityLogType.ObjectFailedAttributeChange]: 'Object Failed Attribute Change',
  [ActivityLogType.ObjectDelete]: 'Object Deleted',
  [ActivityLogType.ObjectsAddRelationship]: 'Relationship Created (to Person)',
  [ActivityLogType.ObjectsDeleteRelationship]: 'Relationship Deleted (to Person)',
  [ActivityLogType.RelationshipAttributeChange]: 'Relationship Attribute Change',
  [ActivityLogType.RelationshipFailedAttributeChange]: 'Relationship Failed Attribute Change',
  [ActivityLogType.DeferredAction]: 'Deferred Action',
  [ActivityLogType.FailedObjectJourneys]: 'Failed Journeys for Object/Relationship Campaign',
};

export const LOG_TYPES_FOR_OBJECTS = [
  ActivityLogType.ObjectAttributeChange,
  ActivityLogType.ObjectFailedAttributeChange,
  ActivityLogType.ObjectCreate,
  ActivityLogType.ObjectDelete,
  ActivityLogType.ObjectsAddRelationship,
  ActivityLogType.ObjectsDeleteRelationship,
];

export const LOG_TYPES_FOR_RELATIONSHIPS_ATTRIBUTES = [
  ActivityLogType.RelationshipAttributeChange,
  ActivityLogType.RelationshipFailedAttributeChange,
];

export const createLogTypeObjectsWithObjectTypes = (objectTypes: ObjectTypeModel[] = [], hiddenTypes: string[]) => {
  let hasObjects = objectTypes.length > 0;
  let options = sortByCaseInsensitive(
    (Object.keys(LogTypeNames) as ActivityLogType[])
      .filter(
        (key) => !(LOG_TYPES_FOR_RELATIONSHIPS_ATTRIBUTES.includes(key) && !hasObjects) && !hiddenTypes.includes(key),
      )
      .flatMap((key) => {
        if (LOG_TYPES_FOR_OBJECTS.includes(key) && hasObjects) {
          return objectTypes.map((objectType) => {
            let label;
            if (key === ActivityLogType.ObjectsAddRelationship || key === ActivityLogType.ObjectsDeleteRelationship) {
              label = `${objectType.singularName} ${LogTypeNames[key]}`;
            } else {
              label = LogTypeNames[key].replace(/^Object/, objectType.singularName);
            }
            return {
              value: key.replace(ActivityLogWildcardRegex, `_o:${objectType.id}:`),
              label,
            };
          });
        }

        return {
          value: key,
          label: LogTypeNames[key],
        };
      }),
    ['label'],
  );

  return options;
};
